<template>
  <v-container fluid class="mto-page">
    <!-- ================= Header ================= -->
    <div class="mto-header">
      <div>
        <h1 class="mto-title">My time off</h1>
        <div class="mto-subtitle">Track your balance, upcoming leave, and request history</div>
      </div>
      <v-btn depressed color="#2A6F97" class="mto-btn-primary" to="/leave-request">
        <v-icon left size="22">mdi-plus</v-icon>
        Request time off
      </v-btn>
    </div>

    <!-- ================= Summary card ================= -->
    <div class="mto-card mto-summary">
      <!-- Available balance -->
      <div class="mto-stat">
        <div class="mto-stat-icon" style="background: #EAF2F8;">
          <v-icon size="30" color="#2A6F97">mdi-calendar-check-outline</v-icon>
        </div>
        <div>
          <div class="mto-stat-label">Available balance</div>
          <div class="mto-stat-value">
            {{ vacation_day }} <span class="mto-stat-unit">{{ Number(vacation_day) === 1 ? "day" : "days" }}</span>
          </div>
          <div class="mto-stat-sub">{{ vacation_hours }} hours</div>
        </div>
      </div>

      <div class="mto-stat-sep"></div>

      <!-- Pending requests -->
      <div class="mto-stat">
        <div class="mto-stat-icon" style="background: #FDF1E3;">
          <v-icon size="30" color="#E08A1E">mdi-alert-circle-outline</v-icon>
        </div>
        <div>
          <div class="mto-stat-label">Pending requests</div>
          <div class="mto-stat-value">{{ pending_cont }}</div>
          <div class="mto-stat-sub">Awaiting approval</div>
        </div>
      </div>

      <div class="mto-stat-sep"></div>

      <!-- Upcoming -->
      <div class="mto-stat">
        <div class="mto-stat-icon" style="background: #EAF2F8;">
          <v-icon size="30" color="#2A6F97">mdi-clock-outline</v-icon>
        </div>
        <div>
          <div class="mto-stat-label">Upcoming</div>
          <div class="mto-stat-value">
            {{ upcoming90.days }} <span class="mto-stat-unit">{{ upcoming90.days === 1 ? "day" : "days" }}</span>
            <span v-if="upcoming90.hours > 0" class="mto-stat-hours">
              {{ upcoming90.hours }} <span class="mto-stat-hours-unit">{{ upcoming90.hours === 1 ? "hour" : "hours"
                }}</span>
            </span>
          </div>
          <div class="mto-stat-sub">Next 90 days</div>
        </div>
      </div>

      <div class="mto-stat-sep"></div>

      <!-- Public holidays -->
      <div class="mto-stat">
        <div class="mto-stat-icon" style="background: #EAF2F8;">
          <v-icon size="30" color="#2A6F97">mdi-calendar-star</v-icon>
        </div>
        <div>
          <div class="mto-stat-label">Public holidays</div>
          <div class="mto-stat-value">{{ public_cont }}</div>
          <div class="mto-stat-sub">This year</div>
        </div>
      </div>
    </div>

    <!-- ================= Time off history ================= -->
    <div class="mto-history-head">
      <div class="mto-history-title-wrap">
        <div class="mto-section-title">Time off history</div>
        <div class="mto-subtitle-sm">View and manage your submitted requests</div>
      </div>

      <div class="mto-filters">
        <v-text-field v-model="filterSearch" outlined dense hide-details clearable placeholder="Search requests"
          prepend-inner-icon="mdi-magnify" class="mto-filter mto-filter-search"></v-text-field>
        <v-select v-model="filterType" :items="leaveTypeOptions" outlined dense hide-details
          class="mto-filter mto-filter-type" :menu-props="{ offsetY: true }"></v-select>
        <v-select v-model="filterStatus" :items="statusOptions" outlined dense hide-details
          class="mto-filter mto-filter-status" :menu-props="{ offsetY: true }"></v-select>
        <v-select v-model="filterYear" :items="yearOptions" outlined dense hide-details
          class="mto-filter mto-filter-year" :menu-props="{ offsetY: true }"></v-select>
        <a class="mto-link mto-reset" @click="resetFilters">Reset</a>
      </div>
    </div>

    <div class="mto-card mto-table-card">
      <div class="mto-table-wrap">
        <table class="mto-table">
          <thead>
            <tr>
              <th>Leave type</th>
              <th>Date</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody v-if="paginatedItems.length > 0">
            <tr v-for="item in paginatedItems" :key="item.id" class="mto-row"
              @click="openLeave(item.id, item.status, item.type_count)">
              <td>
                <div class="d-flex align-center">
                  <div class="mto-type-icon" :style="{ background: leaveTypeIcon(item.leave_type).bg }">
                    <v-icon size="20" :color="leaveTypeIcon(item.leave_type).color">
                      {{ leaveTypeIcon(item.leave_type).icon }}
                    </v-icon>
                  </div>
                  <span class="ml-4">{{ item.leave_type || "-" }}</span>
                </div>
              </td>
              <td>{{ leaveDateText(item) }}</td>
              <td>{{ formatLeaveDuration(item.hours_amount) }}</td>
              <td>
                <span class="mto-chip" :class="statusChipClass(item)">{{ item.status_name || "-" }}</span>
              </td>
              <td>{{ submittedText(item) }}</td>
              <td>
                <div class="d-flex align-center">
                  <a class="mto-link" @click.stop="openLeave(item.id, item.status, item.type_count)">
                    View details
                    <v-icon v-if="!isPending(item)" size="20" color="#2A6F97">mdi-chevron-right</v-icon>
                  </a>
                  <!-- ปุ่ม Cancel แสดงเฉพาะใบลาที่ Pending -->
                  <template v-if="isPending(item)">
                    <span class="mto-action-sep"></span>
                    <a class="mto-link" @click.stop="cancelLeave(item)">Cancel</a>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="6" class="mto-empty">
                {{ leavehistory_list.length > 0 ? "No requests match your filters" : "No Upcoming Time Off" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="mto-pagination">
        <div class="mto-muted">
          Showing {{ showingFrom }}–{{ showingTo }} of {{ filteredItems.length }}
          {{ filteredItems.length === 1 ? "request" : "requests" }}
        </div>

        <div class="mto-pager">
          <button class="mto-page-btn" :disabled="page === 1" @click="previousPage">
            <v-icon size="20">mdi-chevron-left</v-icon>
          </button>
          <template v-for="(p, i) in pageButtons">
            <span v-if="p === '...'" :key="'e' + i" class="mto-page-ellipsis">…</span>
            <button v-else :key="'p' + p" class="mto-page-btn" :class="{ 'mto-page-active': p === page }"
              @click="page = p">
              {{ p }}
            </button>
          </template>
          <button class="mto-page-btn" :disabled="page === pageCount" @click="nextPage">
            <v-icon size="20">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div class="d-flex align-center">
          <span class="mto-muted mr-3">Rows per page:</span>
          <v-select v-model="itemsPerPage" :items="rowsPerPageOptions" outlined dense hide-details
            class="mto-filter mto-rows-select" :menu-props="{ offsetY: true, top: true }"></v-select>
        </div>
      </div>
    </div>

    <!-- ================= Dialog ยืนยันยกเลิกใบลา ================= -->
    <v-dialog v-model="cancelDialog" max-width="440" persistent>
      <div class="mto-dialog">
        <div class="mto-dialog-icon">
          <v-icon size="30" color="#D93025">mdi-calendar-remove-outline</v-icon>
        </div>
        <div class="mto-dialog-title">Cancel this request?</div>
        <div v-if="cancelTarget" class="mto-dialog-detail">
          <b>{{ cancelTarget.leave_type }}</b> · {{ leaveDateText(cancelTarget) }} ·
          {{ formatLeaveDuration(cancelTarget.hours_amount) }}
        </div>
        <div class="mto-dialog-text">
          The request will be removed and the time off will be returned to your balance. This action cannot be undone.
        </div>
        <div class="mto-dialog-actions">
          <v-btn outlined depressed class="mto-dialog-btn mto-dialog-btn-outline" :disabled="cancelLoading"
            @click="closeCancelDialog">
            Keep request
          </v-btn>
          <v-btn depressed color="#D93025" class="mto-dialog-btn mto-dialog-btn-danger" :loading="cancelLoading"
            @click="confirmCancelLeave">
            Yes, cancel request
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="toast.show" :color="toast.color" timeout="3000" top>
      {{ toast.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import api from "@/services/api";
import { server } from "@/services/constants";
import { language } from "../language/Leavelist.js";
import unity from "@/unity/unity";
import moment from "moment";

const ALL_TYPES = "All leave types";
const ALL_STATUSES = "All statuses";
const ALL_YEARS = "All years";

export default {
  name: "leave-list",
  data(vm) {
    return {
      page: 1, // หน้าเริ่มต้น
      itemsPerPage: 20, // จำนวนรายการต่อหน้า
      rowsPerPageOptions: [10, 20, 50, 100],
      // ===== ตัวกรอง (ใหม่ - กรองฝั่งหน้าเว็บจากข้อมูลเดิม) =====
      filterSearch: "",
      filterType: ALL_TYPES,
      filterStatus: ALL_STATUSES,
      filterYear: ALL_YEARS,
      // ===== ข้อมูลเดิม =====
      leavehistory_list: [],
      search_status: "",
      day_sick: 0,
      day_sick_h: 0,
      day_business: 0,
      day_business_h: 0,
      day_vacation: 0,
      day_vacation_h: 0,
      day_oth: 0,
      day_oth_h: 0,
      day_use_leave: 0,
      day_use_leave_d: 0,
      day_use_leave_h: 0,
      menu: false,
      menu2: false,
      search: "",
      toggleEnable: false,
      selectstatus: "",
      status: ["Request", "Approve", "Inprocess", "Reject"],
      datefrom: null,
      dateto: null,
      dateCurrent: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      seticonsearchmore: { icon: "mdi mdi-chevron-down", action: false },
      setlanguage: language.en,
      defaultlanguage: "en",
      chooselanguage: [
        { name: "en", flag: "gb" },
        { name: "th", flag: "th" },
      ],
      aa: "สวัสดี",
      dialogEdit: false,
      dialogDelete: false,
      mDataArray: [],
      position: [],
      approver: [],
      department: [],
      department_list: [],
      position_list: [],
      company: [],
      authorize: [],
      headers: [
        { text: "No.", value: "id", class: "bg-colorth tabletextwhite", width: "50px", sortable: false },
        { text: "Date from", value: "startDate2", class: "bg-colorth tabletextwhite" },
        { text: "Date to", value: "endDate2", class: "bg-colorth tabletextwhite" },
        { text: "Requested", value: "min_amount", class: "bg-colorth tabletextwhite" },
        { text: "Leave Type", value: "leave_type", class: "bg-colorth tabletextwhite" },
        { text: "Status", value: "status_name", class: "bg-colorth tabletextwhite" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      dataAll: [],
      TypeLeaveArray: [],
      vacation_day: 0,
      vacation_hours: 0,
      approver_day: 0,
      approver_hours: 0,
      pending_cont: 0,
      public_cont: 0,
      holiday_dates: [], // วันหยุดบริษัท (YYYY-MM-DD) ใช้ตัดออกตอนนับ Upcoming
      // ===== ยกเลิกใบลา =====
      cancelDialog: false,
      cancelTarget: null,
      cancelLoading: false,
      toast: { show: false, text: "", color: "success" },
    };
  },

  async beforeCreate() {
    this.$store.state.navMenu = true;
  },

  computed: {
    // ===== Upcoming: วันลาที่อนุมัติแล้ว และอยู่ในช่วง วันนี้ ถึง อีก 90 วัน =====
    // ถ้าใบลาคร่อมช่วง 90 วัน จะนับเฉพาะวันทำงาน (จ.-ศ.) ที่อยู่ในช่วง ตามสัดส่วนชั่วโมงของใบลา
    upcoming90() {
      const today = moment().startOf("day");
      const limit = moment(today).add(90, "days");
      let totalHours = 0;

      this.leavehistory_list.forEach((item) => {
        if (!this.isApproved(item)) return;
        const { start, end: rawEnd } = this.leaveRange(item);
        if (!start) return;
        const end = rawEnd || start;
        if (end.isBefore(today, "day") || start.isAfter(limit, "day")) return; // ไม่อยู่ในช่วง

        const hours = Number(item.hours_amount) || 0;
        const allDays = this.countWeekdays(start, end);
        const from = moment.max(start, today);
        const to = moment.min(end, limit);
        const inDays = this.countWeekdays(from, to);
        // allDays = 0 คือใบลาที่ตกวันหยุด/เสาร์-อาทิตย์ทั้งหมด → ไม่นับ
        totalHours += allDays > 0 ? (hours * inDays) / allDays : 0;
      });

      totalHours = Math.round(totalHours * 100) / 100;
      const days = Math.floor(totalHours / 8);
      const hours = Math.round((totalHours - days * 8) * 100) / 100;
      return { days, hours, totalHours };
    },

    computeddatefromFormatted() {
      return unity.formatDate(this.datefrom);
    },
    computeddateToFormatted() {
      return unity.formatDate(this.dateto);
    },
    itemsForSelected() {
      if (this.search.length) {
        return this.dataSource.filter((item) => this.search.includes(item));
      }
      return this.dataSource;
    },

    // ===== ตัวเลือกใน dropdown (สร้างจากข้อมูลจริง) =====
    leaveTypeOptions() {
      const types = [...new Set(this.leavehistory_list.map((i) => i.leave_type).filter(Boolean))].sort();
      return [ALL_TYPES, ...types];
    },
    statusOptions() {
      const st = [...new Set(this.leavehistory_list.map((i) => i.status_name).filter(Boolean))].sort();
      return [ALL_STATUSES, ...st];
    },
    yearOptions() {
      const years = new Set([new Date().getFullYear()]);
      this.leavehistory_list.forEach((i) => {
        const y = this.leaveYear(i);
        if (y) years.add(y);
      });
      return [ALL_YEARS, ...[...years].sort((a, b) => b - a)];
    },

    // ===== กรองข้อมูล =====
    filteredItems() {
      const q = String(this.filterSearch || "").trim().toLowerCase();
      return this.leavehistory_list.filter((item) => {
        if (this.filterType !== ALL_TYPES && item.leave_type !== this.filterType) return false;
        if (this.filterStatus !== ALL_STATUSES && item.status_name !== this.filterStatus) return false;
        if (this.filterYear !== ALL_YEARS && this.leaveYear(item) !== this.filterYear) return false;
        if (q) {
          const text = [
            item.leave_type,
            item.status_name,
            item.data_date,
            item.description,
            this.leaveDateText(item),
            this.submittedText(item),
          ]
            .join(" ")
            .toLowerCase();
          if (!text.includes(q)) return false;
        }
        return true;
      });
    },

    pageCount() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage)); // คำนวณจำนวนหน้าทั้งหมด
    },
    paginatedItems() {
      const start = (this.page - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end); // ดึงรายการที่ต้องแสดงในแต่ละหน้า
    },
    showingFrom() {
      return this.filteredItems.length === 0 ? 0 : (this.page - 1) * this.itemsPerPage + 1;
    },
    showingTo() {
      return Math.min(this.page * this.itemsPerPage, this.filteredItems.length);
    },
    // ปุ่มเลขหน้า (แสดงไม่เกิน 5 ปุ่ม + …)
    pageButtons() {
      const total = this.pageCount;
      const cur = this.page;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      const start = Math.max(2, cur - 1);
      const end = Math.min(total - 1, cur + 1);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < total - 1) pages.push("...");
      pages.push(total);
      return pages;
    },
  },

  watch: {
    filterSearch() { this.page = 1; },
    filterType() { this.page = 1; },
    filterStatus() { this.page = 1; },
    filterYear() { this.page = 1; },
    itemsPerPage() { this.page = 1; },
  },

  async mounted() {
    await api.checkVersion();
    this.userId = localStorage.getItem(server.USER_ID);
    let yourUrlString = window.location;

    let parser = document.createElement("a");
    parser.href = yourUrlString;

    this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

    if (this.authorize_id == null || this.authorize_id == 0) {
      this.$store.state.global_dialog = true;
      this.setupAlertDialog(
        true,
        "Authorize Failed",
        "Please Logout And Login Again",
        "text-h5 red--text text-center"
      );
      this.$router.back();
    }

    const router_path = parser.pathname.replace("/", "");

    const res_auth = await api.getAuthorize(this.userId, router_path);

    this.authorize_view = res_auth.data[0].smd_view >= 1 ? true : false;
    this.authorize_add = res_auth.data[0].smd_add >= 1 ? true : false;
    this.authorize_edit = res_auth.data[0].smd_edit >= 1 ? true : false;
    this.authorize_del = res_auth.data[0].smd_del >= 1 ? true : false;

    if (!this.authorize_view) {
      this.$router.push("/not-found");
    }

    // ----------------- Check Authorize ---------------------------

    let comp_id = localStorage.getItem(server.COMPANYID);

    this.userId = localStorage.getItem(server.USER_ID);
    if (this.userId && api.isLoggedIn()) {
      // this.$router.push("/");
    } else {
      this.$store.state.isLogged = false;
      this.$router.push("/login");
    }
    await this.loadQuotaType();
    await this.loadleaveHistory();
    this.$hideLoader();
  },
  methods: {
    async loadleaveHistory() {
      const res_leave_history = await api.getListHistoryAll(
        localStorage.getItem(server.USER_ID)
      );

      if (res_leave_history.data && res_leave_history.data.length > 0) {
        const filteredData = res_leave_history.data.filter(
          (item) => Number(item.status) === 1 || Number(item.status) === 2,
        );
        this.pending_cont = filteredData.length;
      } else {
        this.pending_cont = 0;
      }

      for (const [index, item] of res_leave_history.data.entries()) {
        if (item.leave_type == "Sick Leave") {
          item.leave_type = "Sick";
        } else if (item.leave_type == "Vacation Leave") {
          item.leave_type = "Vacation";
        }
        var leave_day = 0;
        var leave_hours = 0;

        leave_day = Math.floor(Number(item.hours_amount) / 8);
        leave_hours = Number(item.hours_amount) - leave_day * 8;

        if (leave_day == 0) {
          if (leave_hours == 0) {
            item.description = `${item.data_date}`;
          } else if (leave_hours == 4) {
            item.description = `${item.data_date} (half day)`;
          } else if (leave_hours == 8) {
            item.description = `${item.data_date} (full day)`;
          } else {
            item.description = `${item.data_date} (${leave_hours} hours)`;
          }
        } else {
          if (leave_hours == 0) {
            if (leave_day > 1) {
              item.description = `${item.data_date} (${leave_day} day)`;
            } else {
              item.description = `${item.data_date} (full day)`;
            }
          } else {
            item.description = `${item.data_date} (${leave_day} day ${leave_hours} hours)`;
          }
        }
      }
      this.leavehistory_list = res_leave_history.data;
      console.log("this.leavehistory_list");
      console.log(this.leavehistory_list);
    },
    async loadQuotaType() {
      const res_quota = await api.getListByUserDashboard(
        localStorage.getItem(server.USER_ID)
      );
      if (res_quota.data && res_quota.data.length > 0) {
        var canuse_leave = 0;
        canuse_leave = Number(res_quota.data[0].leave_can_use);
        this.vacation_day = Math.floor(canuse_leave);
        this.vacation_hours = (canuse_leave - this.vacation_day) * 8;
      } else {
        this.vacation_day = 0;
        this.vacation_hours = 0;
      }

      const res_holiday = await api.getAllYear(
        Number(localStorage.getItem(server.COMPANYID))
      );
      if (res_holiday.data && res_holiday.data.length > 0) {
        this.public_cont = res_holiday.data.length;
        // เก็บวันหยุดไว้ใช้ตัดออกตอนนับ Upcoming
        this.holiday_dates = res_holiday.data.map((h) => this.holidayDateKey(h)).filter(Boolean);
        console.log("holiday sample:", res_holiday.data[0], "→ holiday_dates:", this.holiday_dates);
      } else {
        this.public_cont = 0;
        this.holiday_dates = [];
      }

      const res_approval = await api.getAllByApproval(
        Number(localStorage.getItem(server.USER_ID))
      );
      if (res_approval.data && res_approval.data.length > 0) {
        this.approver_day = Math.floor(
          Number(res_approval.data[0].amount) / 60 / 8
        );
        this.approver_hours = Math.floor(
          (Number(res_approval.data[0].amount) - this.approver_day * 60 * 8) /
          60
        );
      } else {
        this.approver_day = 0;
        this.approver_hours = 0;
      }
    },
    async openLeave(id, status, type_count) {
      if (status == 1) {
        if (type_count) {
          this.$router.push(`/leave-request-edit/${id}`);
        } else {
          this.$router.push(`/leave-request-waiting/${id}/leave`);
        }
      } else {
        this.$router.push(`/leave-request-waiting/${id}/leave`);
      }
    },
    previousPage() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    nextPage() {
      if (this.page < this.pageCount) {
        this.page += 1;
      }
    },

    // ================= UI helpers (ใหม่) =================
    resetFilters() {
      this.filterSearch = "";
      this.filterType = ALL_TYPES;
      this.filterStatus = ALL_STATUSES;
      this.filterYear = ALL_YEARS;
      this.page = 1;
    },
    leaveTypeIcon(type) {
      const t = String(type || "").toLowerCase();
      if (t.includes("sick")) return { icon: "mdi-thermometer", color: "#3C8D4F", bg: "#EAF4EC" };
      if (t.includes("vacation")) return { icon: "mdi-briefcase-outline", color: "#2A6F97", bg: "#EAF2F8" };
      if (t.includes("personal") || t.includes("business"))
        return { icon: "mdi-account-outline", color: "#6B4FA0", bg: "#F0ECF7" };
      return { icon: "mdi-calendar-blank-outline", color: "#6B7280", bg: "#F1F3F5" };
    },
    // Pending: status = 1 หรือ status_name เป็น Pending
    isPending(item) {
      return Number(item.status) === 1 || String(item.status_name || "").toLowerCase().includes("pending");
    },
    // ================= ยกเลิกใบลา (logic เดียวกับหน้า Edit: onCancelSave) =================
    // กดปุ่ม Cancel ในตาราง -> เปิด dialog ยืนยัน
    cancelLeave(item) {
      this.cancelTarget = item;
      this.cancelDialog = true;
    },
    closeCancelDialog() {
      if (this.cancelLoading) return;
      this.cancelDialog = false;
      this.cancelTarget = null;
    },
    showToast(text, color) {
      this.toast = { show: true, text, color };
    },
    // ยืนยันยกเลิก
    async confirmCancelLeave() {
      const item = this.cancelTarget;
      if (!item) return;
      const hisId = item.id;
      const userId = localStorage.getItem(server.USER_ID);
      const companyId = localStorage.getItem(server.COMPANYID);

      this.cancelLoading = true;
      this.$showLoader();

      const fail = (msg) => {
        this.cancelLoading = false;
        this.cancelDialog = false;
        this.cancelTarget = null;
        this.$hideLoader();
        this.showToast(msg || "Unable to cancel this request. Please try again.", "error");
      };

      try {
        // 1. ดึงรายละเอียดใบลา -> quota ที่ใช้ไป (เหมือน loadLeaveHistory หน้า Edit)
        const res_his = await api.getLeaveHistoryByHistoryID(hisId);
        if (!res_his.data || res_his.data.length === 0) return fail("Leave request not found.");

        // รวมนาทีที่ต้องคืนต่อ quota (รองรับลาข้าม period)
        const returnMap = {};
        res_his.data.forEach((d) => {
          const lq = d.lq_id_detail;
          if (lq === null || lq === undefined || lq === "") return;
          returnMap[lq] = (returnMap[lq] || 0) + (Number(d.amount_detail) || 0);
        });
        const lqIds = Object.keys(returnMap);
        if (lqIds.length === 0) return fail();

        // 2. ดึงโควต้าปัจจุบัน แล้วคำนวณ use_leave หลังคืน (เหมือน loaddataLeaveUsed หน้า Edit)
        const res_quota = await api.getListUseByUserIDV2byCompany(userId, companyId);
        const quotas = res_quota.data || [];
        const newUseLeave = {};
        lqIds.forEach((lq) => {
          const q = quotas.find((x) => x.id == lq);
          const used = q ? Number(q.use_leave) || 0 : 0;
          newUseLeave[lq] = used - returnMap[lq] / 60 / 8;
        });

        // 3. ลบใบลา (ลำดับเดียวกับหน้า Edit)
        const res_dlq = await api.delete_leaveH(hisId);
        if (!res_dlq.data || !res_dlq.data.id) return fail();
        await api.delete_leaveQHD(hisId);
        await api.delete_leaveAD(hisId);

        // 4. คืนโควต้าทีละ quota
        for (const lq of lqIds) {
          const res_ulq = await api.updateQuota(lq, { use_leave: newUseLeave[lq] });
          if (!(res_ulq.status == 200 || res_ulq.status == 201)) return fail();
        }

        // 5. โหลดข้อมูลใหม่
        await this.loadQuotaType();
        await this.loadleaveHistory();
        if (this.page > this.pageCount) this.page = this.pageCount;

        this.cancelLoading = false;
        this.cancelDialog = false;
        this.cancelTarget = null;
        this.$hideLoader();
        this.showToast("Your request has been canceled.", "success");
      } catch (error) {
        console.error("❌ Error in confirmCancelLeave:", error);
        fail();
      }
    },
    // อนุมัติแล้ว: status = 3 หรือ status_name เป็น Approved
    isApproved(item) {
      return Number(item.status) === 3 || String(item.status_name || "").toLowerCase().includes("approve");
    },
    // นับวันทำงานระหว่าง 2 วัน (รวมวันเริ่ม-วันจบ) ไม่นับ เสาร์-อาทิตย์ และวันหยุดบริษัท
    countWeekdays(start, end) {
      let n = 0;
      const d = moment(start).startOf("day");
      const e = moment(end).startOf("day");
      while (d.isSameOrBefore(e, "day")) {
        const isWeekend = d.day() === 0 || d.day() === 6;
        const isHoliday = this.holiday_dates.includes(d.format("YYYY-MM-DD"));
        if (!isWeekend && !isHoliday) n++;
        d.add(1, "day");
      }
      return n;
    },
    // แปลงวันที่วันหยุดจาก API เป็น YYYY-MM-DD (รองรับหลายชื่อ field)
    holidayDateKey(h) {
      const raw = h.h_date || h.holiday_date || h.hdate || h.date || h.start_date || h.h_start;
      const m = this.parseDate(raw);
      return m ? m.format("YYYY-MM-DD") : null;
    },
    statusChipClass(item) {
      const s = String(item.status_name || "").toLowerCase();
      if (s.includes("approve")) return "mto-chip-success";
      if (s.includes("reject")) return "mto-chip-danger";
      if (s.includes("cancel")) return "mto-chip-info";
      return "mto-chip-warning";
    },
    // ระยะเวลา: 8 ชม. = Full day, น้อยกว่า = X hours, มากกว่า = X days
    formatLeaveDuration(hoursAmount) {
      const total = Number(hoursAmount) || 0;
      if (total <= 0) return "-";
      const d = Math.floor(total / 8);
      const h = Number((total - d * 8).toFixed(2));
      if (d === 1 && h === 0) return "Full day";
      const parts = [];
      if (d > 0) parts.push(`${d} ${d === 1 ? "day" : "days"}`);
      if (h > 0) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
      return parts.join(" ");
    },
    parseDate(value) {
      if (!value) return null;
      const s = String(value).trim();
      if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
        const m = moment(s.substring(0, 10), "DD/MM/YYYY");
        return m.isValid() ? m : null;
      }
      const m = moment(s);
      return m.isValid() ? m : null;
    },
    // วันเริ่ม/สิ้นสุด: ใช้ str_time/end_time ถ้ามี ไม่งั้นแยกจาก data_date ("DD/MM/YYYY - DD/MM/YYYY")
    leaveRange(item) {
      let start = this.parseDate(item.str_time || item.startDate || item.start_date);
      let end = this.parseDate(item.end_time || item.endDate || item.end_date);
      if (!start && item.data_date) {
        const [a, b] = String(item.data_date).split(" - ");
        start = this.parseDate(a);
        end = this.parseDate(b);
      }
      return { start, end };
    },
    leaveYear(item) {
      const { start } = this.leaveRange(item);
      return start ? start.year() : null;
    },
    leaveDateText(item) {
      const { start, end } = this.leaveRange(item);
      if (!start) return item.data_date || "-";
      if (!end || end.isSame(start, "day")) return start.format("D MMM YYYY");
      return `${start.format("D MMM YYYY")} - ${end.format("D MMM YYYY")}`;
    },
    // วันที่ส่งคำขอ (รองรับหลายชื่อ field จาก API)
    submittedText(item) {
      const raw =
        item.create_date || item.created_at || item.createdate || item.create_at ||
        item.created_date || item.request_date || item.createdAt;
      const m = this.parseDate(raw);
      return m ? m.format("D MMM YYYY") : "-";
    },
  },
};
</script>

<style scoped>
/* ================= Page ================= */
.mto-page {
  padding: 16px 32px 32px;
  color: #1F2937;
}

.mto-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.mto-title {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
  color: #111827;
  margin: 0;
}

.mto-subtitle {
  font-size: 15px;
  color: #4B5563;
  margin-top: 4px;
}

.mto-subtitle-sm {
  font-size: 14px;
  color: #4B5563;
  margin-top: 2px;
}

.mto-btn-primary {
  color: #FFFFFF !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  height: 46px !important;
  padding: 0 24px !important;
  border-radius: 8px !important;
}

/* ================= Card ================= */
.mto-card {
  background: #FFFFFF;
  border: 1px solid #E8ECF1;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

/* ================= Summary ================= */
.mto-summary {
  display: flex;
  align-items: stretch;
  padding: 24px 28px;
  gap: 24px;
}

.mto-stat {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.mto-stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mto-stat-label {
  font-size: 14px;
  color: #4B5563;
}

.mto-stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
}

.mto-stat-unit {
  font-size: 20px;
  font-weight: 600;
}

/* ส่วนชั่วโมงใน Upcoming: เล็กกว่า days */
.mto-stat-hours {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin-left: 6px;
}

.mto-stat-hours-unit {
  font-size: 14px;
  font-weight: 500;
}

.mto-stat-sub {
  font-size: 14px;
  color: #4B5563;
}

.mto-stat-sep {
  width: 1px;
  background: #E5E7EB;
  flex-shrink: 0;
}

/* ================= History header / filters ================= */
.mto-history-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin: 32px 0 20px;
}

.mto-section-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.mto-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.mto-filter {
  background: #FFFFFF;
  font-size: 14px;
}

.mto-filter-search {
  width: 220px;
}

.mto-filter-type {
  width: 180px;
}

.mto-filter-status {
  width: 160px;
}

.mto-filter-year {
  width: 120px;
}

.mto-rows-select {
  width: 84px;
}

.mto-filter>>>.v-input__slot {
  min-height: 42px !important;
  border-radius: 6px !important;
}

.mto-filter>>>fieldset {
  border-color: #D1D5DB !important;
}

.mto-link {
  color: #2A6F97 !important;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.mto-link:hover {
  text-decoration: underline;
}

.mto-action-sep {
  width: 1px;
  height: 22px;
  background: #D1D5DB;
  margin: 0 20px;
}

.mto-reset {
  margin-left: 8px;
}

/* ================= Table ================= */
.mto-table-card {
  overflow: hidden;
}

.mto-table-wrap {
  overflow-x: auto;
}

.mto-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.mto-table thead th {
  background: #FFFFFF;
  color: #374151;
  font-weight: 400;
  font-size: 14px;
  text-align: left;
  padding: 16px 24px;
  border-bottom: 1px solid #E8ECF1;
  white-space: nowrap;
}

.mto-table tbody td {
  padding: 11px 24px;
  border-bottom: 1px solid #EEF1F4;
  color: #111827;
  white-space: nowrap;
}

.mto-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.mto-row:hover {
  background-color: #F3F8FC;
}

.mto-type-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mto-chip {
  display: inline-block;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.mto-chip-success {
  background: #E8F3EA;
  color: #2E7D32;
}

.mto-chip-danger {
  background: #FDE8E8;
  color: #C62828;
}

.mto-chip-warning {
  background: #FDF0DC;
  color: #E08A1E;
}

.mto-chip-info {
  background: #E6EEF7;
  color: #2A5F8F;
}

.mto-empty {
  text-align: center;
  padding: 48px 16px !important;
  font-size: 16px;
  color: #6B7280 !important;
}

/* ================= Pagination ================= */
.mto-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 24px;
}

.mto-muted {
  color: #4B5563;
  font-size: 14px;
}

.mto-pager {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mto-page-btn {
  min-width: 36px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #111827;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mto-page-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.mto-page-active {
  background: #2A6F97;
  border-color: #2A6F97;
  color: #FFFFFF;
}

.mto-page-ellipsis {
  color: #6B7280;
  padding: 0 2px;
}

/* ================= Cancel dialog ================= */
.mto-dialog {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 28px 28px 24px;
  text-align: center;
}

.mto-dialog-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #FDE8E8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.mto-dialog-title {
  font-size: 19px;
  font-weight: 700;
  color: #111827;
}

.mto-dialog-detail {
  font-size: 14px;
  color: #374151;
  margin-top: 8px;
}

.mto-dialog-text {
  font-size: 13px;
  color: #6B7280;
  margin-top: 10px;
  line-height: 1.5;
}

.mto-dialog-actions {
  display: flex;
  gap: 12px;
  margin-top: 22px;
}

.mto-dialog-btn {
  flex: 1 1 0;
  height: 42px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
}

.mto-dialog-btn-outline {
  border-color: #E5E7EB !important;
  color: #111827 !important;
}

.mto-dialog-btn-danger {
  color: #FFFFFF !important;
}

/* ================= Responsive ================= */
@media (max-width: 1264px) {
  .mto-summary {
    flex-wrap: wrap;
  }

  .mto-stat {
    flex: 1 1 40%;
  }

  .mto-stat-sep {
    display: none;
  }
}

@media (max-width: 600px) {
  .mto-page {
    padding: 8px 12px 24px;
  }

  .mto-stat {
    flex: 1 1 100%;
  }

  .mto-filter-search,
  .mto-filter-type,
  .mto-filter-status,
  .mto-filter-year {
    width: 100%;
  }

  .mto-pagination {
    justify-content: center;
  }
}
</style>